/**
 * IndividualReviewBody — the CONSOLIDATED Individual surface (Personal path).
 *
 * Person-Modal consolidation (Bill 2026-06-11, LOCKED mockup v6 —
 * docs/operations/person-modal-inventory/06-INDIVIDUAL-SURFACE-CLEANUP-MOCKUP.html):
 * the old Stage-2 cohort screen + separate docs-grid stage collapsed INTO this
 * body. Section order: Citizenship block (top) → Documents-to-capture tile
 * grid → CIP → Profile/SSN-TIN (+ NRA Alien_CType ceremony) → Work-School →
 * Address & Contact (hosts the conditional Address Verification tile) →
 * Verify. Jump-scroll pills: Citizenship · Documents · CIP · Profile ·
 * Work-School · Contact · Verify. The Origin section is GONE.
 *
 * Mounted by BOTH:
 *   - The legacy IndividualPersonDialog (wraps it in <ModalShell> +
 *     PdwFooterDyad for direct/back-compat callers — RCM Maint CIF "Edit
 *     CIF" + JointOwnerAddDialog flows still route here via PersonDialog).
 *   - PdwShell at its `'review'` stage — slotted inline, with the shell
 *     owning the outer modal frame + Return/Continue footer. Personal
 *     fresh-add now lands here DIRECTLY from the bucket pick (citizenship
 *     unset, D2).
 *
 * Bill 2026-05-26 — BACKLOG-0070 (v0.14.48). Resolves the "disjointed
 * breadcrumb" called out by `feedback_pdw_breadcrumb_disjointed_must_match_
 * spec_20260513`: the cohort gate + (later) docs grid + scan loop now live
 * in one persistent shell, with this body as the Stage 9 panel.
 *
 * Owns:
 *   - local draft state (mirrors the prop `person` on mount + edits)
 *   - per-tab state (PdwTabStrip status pills derived from draft fields)
 *   - attachedDocs (CIP capture + uploads bubble up through this state)
 *   - isStudentVisa toggle (Citizenship → Employment school-override)
 *   - derivation hook (customerType / supervisor gate)
 *   - existing-customer detection + Verify-tab status derivation
 *     (BACKLOG-0513 / 0521 — LN/OFAC auto-run suppression is owned by the
 *     IndividualAmlInsightSection itself; this body just renders it.)
 *
 * Does NOT own:
 *   - modal frame (ModalShell) — provided by caller
 *   - footer (PdwFooterDyad) — provided by caller
 *   - save dispatch — exposed via `onDraftChange(draft, meta)` so the
 *     caller can read the latest draft + dup/supervisor gate metadata and
 *     decide when to call its own onSave
 *
 * The save action stays with the caller because:
 *   - In IndividualPersonDialog (legacy), Continue/Save on the final tab
 *     calls `onSave(draft)` and closes the modal.
 *   - In PdwShell (new), the entire shell's terminal Continue saves the
 *     draft + commits the cart row + closes the shell.
 *
 * Both consume the body identically.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ModalShell } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import type { RaoPerson } from '../../../types';
import { PdwStatusBadges } from '../PdwStatusBadges';
import { PdwTabStrip, type PdwTabKey, type PdwTabState } from '../PdwTabStrip';
import { DerivedSectionCard, type DerivedRow } from '../DerivedSectionCard';
import {
  detectCrossDocConflicts,
  type CapturedDocForConflict,
  type ConflictFinding,
} from '../captureStub';
import { PdwStage4DocsGrid } from '../PdwStage4DocsGrid';
import { IndividualCipScanSection } from './IndividualCipScanSection';
import { IndividualIdentitySection } from './IndividualIdentitySection';
import { IndividualEmploymentSection, j2EadGatePending } from './IndividualEmploymentSection';
import { IndividualCitizenshipSection } from './IndividualCitizenshipSection';
import { IndividualNraSection } from './IndividualNraSection';
import { IndividualAmlInsightSection } from './IndividualAmlInsightSection';
import { PepDesignationCard } from './PepDesignationCard';
import { AddressVerificationTile } from './AddressVerificationTile';
import { isIneligibleVisaClass } from './individual-config';
import { useIndividualDerivation } from './useIndividualDerivation';

import { AddressSection } from '../shared/AddressSection';
import { ContactSection } from '../shared/ContactSection';
import { OfacScanSection } from '../shared/OfacScanSection';
import { VerifyRollupBanner } from '../shared/VerifyRollupBanner';
import {
  type AttachedDoc,
  readAttachedDocs,
  mergeAttachedDoc,
  mergeAttachedDocs,
  mergeAttachedDocsKeepExisting,
} from '../shared/attachedDocs';
import { useAttachedDocsHydration } from '../shared/useInquiryHydration';
import {
  deriveVerifyClearanceMap,
  deriveVerifyTabStatus,
  isExistingCustomer,
  readIdExpirationWarningDays,
  readAcceptExpiredGraceMonths,
} from '../shared/existingCustomer';
import { idScanFailFromConflicts } from '../shared/verifyClearance';
import { missingRequiredIndividualFields } from '@/services/rules/requiredIndividualFields';
import { readTestIdentitySettings, testIdentityViolation } from '@/utils/testIdentity';
import { withMiddleInitial } from '../shared/middleInitial';

/**
 * Surface modes — same three the legacy IndividualPersonDialog supports:
 *   - 'review-add' — add existing CIF to cart (post-CustInq review step)
 *   - 'new'        — brand new person entry (New Acct flow / PdwShell)
 *   - 'edit'       — edit existing CIF (New Acct mid-flow OR Maint CIF)
 */
export type IndividualReviewBodyMode = 'review-add' | 'new' | 'edit';

/** Metadata surfaced to the caller alongside every draft change. */
export interface IndividualReviewBodyMeta {
  /** True iff the local draft has diverged from the initial seed. */
  hasChanges: boolean;
  /** True iff this CIF is already on the cart (review-add dup case). */
  dupInCart: boolean;
  /** True iff Save/Continue should be disabled (dup or supervisor gate). */
  saveDisabled: boolean;
  /** Short reason string when saveDisabled — suitable for a tooltip. */
  saveDisabledReason?: string;
  /** Current PdwTabStrip selection (for callers that surface tab in title). */
  activeTab: PdwTabKey;
}

export interface IndividualReviewBodyProps {
  person: RaoPerson;
  mode: IndividualReviewBodyMode;
  /** Cart context — upload metadata for capture persistence. */
  cartId: string;
  /**
   * Cart mode (Production | UAT-Live | Test-Live | Sandbox). Forwarded to the
   * LexisNexis verify call so a TEST cart is billed and recorded against the
   * LN SANDBOX institution rather than the production one. Omitted means the
   * server default, which is Production — see IndividualAmlInsightSection.
   */
  cartMode?: string;
  /** Officer code for upload metadata. Falls back to 'UNKNOWN'. */
  officerCode?: string;
  /** Set of CIF IDs already on the cart — drives review-add dup detection. */
  cartCifIds?: ReadonlyArray<string>;
  /** Disables every input + hides edit controls. */
  readOnly?: boolean;
  /** Hides the CIP scan panel (used in Maint CIF edit). Defaults true. */
  cipScanEnabled?: boolean;
  /**
   * Fired whenever the local draft mutates. Caller stores the latest draft +
   * reads `meta` to gate its own save/continue affordance. Fires on mount
   * with the initial seed + computed meta so callers don't have to mirror
   * the dup/supervisor gate themselves.
   */
  onDraftChange?: (draft: RaoPerson, meta: IndividualReviewBodyMeta) => void;
  /**
   * Initial tab pill to light. Defaults to 'citizenship' — the consolidated
   * body leads with the Citizenship block (Bill 2026-06-11, LOCKED mockup v6).
   */
  initialTab?: PdwTabKey;
  /**
   * E1 (Bill 2026-06-03): fired when a COMPLETE SSN is entered on a fresh-new
   * person, so the host can run the silent dup-CIF check EARLY (when CIF info is
   * entered) instead of only at the final Add. No-op for existing CIFs.
   */
  /** markNoTinCleared (Bill 2026-10-14): the host calls it when Core was searched by name + DOB
   *  for this draft and nothing was found, or the officer dismissed the match — the no-TIN proof. */
  onEarlyDupCheck?: (draft: RaoPerson, markNoTinCleared?: (key: string) => void) => void;
}

export function IndividualReviewBody({
  person,
  mode,
  cartId,
  cartMode,
  officerCode,
  cartCifIds,
  readOnly = false,
  cipScanEnabled = true,
  onDraftChange,
  initialTab = 'citizenship',
  onEarlyDupCheck,
}: IndividualReviewBodyProps): JSX.Element {
  const [local, setLocal] = useState<RaoPerson>(person);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [isStudentVisa, setIsStudentVisa] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<PdwTabKey>(initialTab);
  // D2 — per-section "Mark Reviewed Correct" (review-add ceremony only).
  const [reviewedSections, setReviewedSections] = useState<ReadonlySet<string>>(new Set());
  // D2 — "Review Completed" confirm modal (skip unmarked sections for repeat
  // customers / expediency).
  const [showReviewCompleted, setShowReviewCompleted] = useState<boolean>(false);

  // BACKLOG-0569 / Slot-4 T0 (Contract C2) — attachedDocs is NOT a sibling
  // useState any more. It lives on the REAL draft (`local.extra.attachedDocs`)
  // so it flows through onDraftChange → onSave → peopleJson and survives
  // reopen. Derived here for the badge / tab / conflict reads; the canonical
  // store is `local`.
  const attachedDocs = useMemo<ReadonlyArray<AttachedDoc>>(() => readAttachedDocs(local), [local]);

  const { config } = useConfig();
  const warningDays = readIdExpirationWarningDays(config.systemSettings);
  // C2: accept-expired grace window (default 6mo, Collette) — expired-within-grace
  // renders yellow "Update recommended" instead of a red hard-block.
  const graceMonths = readAcceptExpiredGraceMonths(config.systemSettings);
  const verifyStatus = useMemo(
    () => deriveVerifyTabStatus(local, warningDays, graceMonths),
    [local, warningDays, graceMonths],
  );

  // CIP slot-machine priority (Bill 2026-06-01) — map the global config rows
  // (CipIdPriorityRow: {idTypeCode, rank, isActive, citation}) into the
  // assignCipSlots rule shape (customerType '*' = global; unranked NULL → MAX so
  // it sorts last but is never dropped). Feeds IndividualCipScanSection so a
  // higher-F254-priority capture claims ID1 + bumps the incumbent to ID2.
  const cipIdPriorityRules = useMemo(
    () =>
      (config.cipIdPriority ?? [])
        .filter((r) => r.isActive)
        .map((r) => ({
          customerType: '*',
          idTypeCode: r.idTypeCode,
          rank: r.rank ?? Number.MAX_SAFE_INTEGER,
          isActive: r.isActive,
        })),
    [config.cipIdPriority],
  );
  // F1 (Bill 2026-06-03): a review-add / edit opened on an on-file CIF IS an
  // existing customer even when the hydrated draft lacks a prior LN score /
  // OFAC status / search-result marker (the dup-CIF "Review & Add" path doesn't
  // set those). The dialog mode + a populated cifId is the authoritative signal.
  // Without this, an existing CIF was treated as a fresh add → re-required CIP
  // capture + LN (procedure breach).
  const existingCustomer =
    isExistingCustomer(local) || (mode !== 'new' && !!(local.cifId ?? '').trim());
  // F1 (Bill 2026-06-03): on-file CIP from CustInq hydration (extra.idType seeded
  // at useInquiryHydration L223) satisfies the CIP requirement for an existing
  // CIF — no fresh re-capture required. Drives the CIP-tab "done" state below.
  const hasOnFileCip =
    existingCustomer &&
    typeof (local.extra as Record<string, unknown> | undefined)?.['idType'] === 'string' &&
    ((local.extra as Record<string, unknown>)['idType'] as string).trim() !== '';

  // Contract C4 — cross-document conflict detection. Build the captured-ID set
  // from the draft's CIP fields and run BL's comparator. A `major` finding
  // (e.g. ID numbers disagree across the two IDs) drives a red ALERT + a `fail`
  // on the Verify idScan section; `minor` is BL's FieldComparisonDialog "pick".
  const conflicts = useMemo<ReadonlyArray<ConflictFinding>>(() => {
    const e = (local.extra ?? {}) as Record<string, unknown>;
    const str = (k: string): string => (typeof e[k] === 'string' ? (e[k] as string) : '');
    // BL's comparator takes CapturedDocForConflict (docId + comparable field
    // parts). The two CIP IDs share the same person's name/DOB, so the
    // cross-document signal we can surface from the CIP fields is the ID number
    // disagreeing between ID1 and ID2 (the id-number-mismatch counterfeit rule).
    const idType = str('idType');
    const idType2 = str('idType2');
    const captures: CapturedDocForConflict[] = [];
    if (idType || str('idNumber')) {
      captures.push({ docId: 'GOVT_ID', label: 'Primary ID', idNumber: str('idNumber') });
    }
    // B3 (Bill 2026-06-03): only cross-compare ID NUMBERS when the two CIP slots
    // are the SAME document type. ID1 and ID2 are intentionally DIFFERENT
    // documents (e.g. Driver's License + SSN Card, or DL + Passport) whose
    // numbers differ BY NATURE — comparing them produced a bogus "non-genuine
    // ID" conflict that blocked the ID scan. (Surfaced after v0.27.0 #4 mapped
    // the SS-card's SSN into idNumber2.) The id-number-mismatch counterfeit
    // signal is only meaningful for two captures of the same doc type.
    if ((idType2 || str('idNumber2')) && idType2 !== '' && idType2 === idType) {
      captures.push({ docId: 'GOVT_ID_2', label: '2nd ID', idNumber: str('idNumber2') });
    }
    return detectCrossDocConflicts(captures);
  }, [local]);
  const majorConflicts = useMemo(() => conflicts.filter((c) => c.severity === 'major'), [conflicts]);

  // Bridge BACKLOG-0513 signals → BACKLOG-0017 VerifyClearanceMap so the
  // VerifyRollupBanner shows real per-section state. Explicit FSR overrides
  // already in person.extra.verifyClearance win; derived values fill gaps.
  // C4: a major conflict forces idScan → fail (after derived, so it wins over
  // a derived pass; an explicit FSR override still wins last).
  const personWithDerivedClearance = useMemo<RaoPerson>(() => {
    const derived = deriveVerifyClearanceMap(local, warningDays);
    const conflictFail = idScanFailFromConflicts(majorConflicts);
    const existing = (local.extra as { verifyClearance?: Record<string, unknown> } | undefined)
      ?.verifyClearance ?? {};
    return {
      ...local,
      extra: {
        ...(local.extra ?? {}),
        verifyClearance: {
          ...derived,
          ...(conflictFail ? { idScan: conflictFail } : {}),
          ...existing,
        },
      },
    } as RaoPerson;
  }, [local, warningDays, majorConflicts]);

  // BACKLOG-0511 + Slot-4 T0 — PdwStatusBadges' Identity-Verified axis reads
  // `person.extra.attachedDocs` (photo-ID proxy). Now that attachedDocs live
  // on `local.extra.attachedDocs` (the real draft), `local` IS the badge
  // source — no throwaway memo. (The prior `personForBadges` memo injected
  // docs only for badge rendering and was never the saved object, which is
  // exactly how the docs got dropped on save — BACKLOG-0569.)

  // Slot-4 T0 (Contract C2) — merge a captured/uploaded doc straight into the
  // draft. `mergeAttachedDoc` keys by docCode (re-capture replaces) and writes
  // through the canonical persisted shape, so the doc reaches peopleJson.
  const registerAttachedDoc = useCallback((doc: AttachedDoc): void => {
    setLocal((prev) => mergeAttachedDoc(prev, doc));
    setHasChanges(true);
  }, []);

  // Re-seed local when the parent swaps the person prop (e.g., PdwShell
  // hops from a search-result hydrate into review-add mode). The seed now
  // CARRIES `extra.attachedDocs` (Edit-mode hydration — Slot-4 T5 — populates
  // it), so prior captures survive reopen instead of re-seeding to `[]`.
  useEffect(() => {
    setLocal(person);
    setHasChanges(false);
  }, [person]);

  // Slot-4 T5 — Edit-mode document hydration. For an existing CIF (mode
  // 'edit' / 'review-add'), pull prior captured docs from the v3 Document
  // Library and merge them in (keep-existing, so a fresh in-session capture
  // always wins). Runs once per cifId. Without this, an existing CIF opened
  // in Edit shows zero captures and the Identity-Verified badge can't count
  // the photo-ID even though the image exists in storage.
  const { fetchAttachedDocsForCif } = useAttachedDocsHydration();
  const hydratedCifRef = useRef<string | null>(null);
  useEffect(() => {
    const cifId = (person.cifId ?? '').trim();
    if (mode === 'new' || !cifId || hydratedCifRef.current === cifId) return;
    hydratedCifRef.current = cifId;
    let cancelled = false;
    void (async () => {
      const docs = await fetchAttachedDocsForCif(cifId);
      if (cancelled || docs.length === 0) return;
      setLocal((prev) => mergeAttachedDocsKeepExisting(prev, docs));
    })();
    return () => {
      cancelled = true;
    };
  }, [person.cifId, mode, fetchAttachedDocsForCif]);

  // "Existing" means an EXISTING Core Director customer (picked from search
  // or already bound to a CIF) — those identity anchors (SSN / TIN code /
  // DOB) are locked because edits here would silently diverge from Core
  // (RCM maintenance actions are the sanctioned path). It does NOT mean
  // "the dialog was opened in Edit mode": a brand-new person being edited
  // before commit MUST keep those fields editable — conflating the two
  // locked Bill out of fixing a bad DOB mid-walk (2026-07-02).
  const sectionMode: 'new' | 'existing' =
    local.mode === 'existing' || (local.cifId ?? '').trim() !== '' ? 'existing' : 'new';
  const derivation = useIndividualDerivation(local, {
    mode: sectionMode,
    storedType: person.customerType,
  });

  const update = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const next: RaoPerson = { ...prev, ...withMiddleInitial(patch) };
      const fn = next.firstName ?? '';
      const mn = next.middleName ?? '';
      const ln = next.lastName ?? '';
      const display = [fn, mn, ln].filter((x) => !!x).join(' ').trim();
      if (display) next.fullName = display;
      if (prev.mode === 'existing') next.dirty = true;
      return next;
    });
    setHasChanges(true);
  }, []);

  // Person-Modal consolidation (Bill 2026-06-11) — capture patches from the
  // hosted PdwStage4DocsGrid + AddressVerificationTile land through this
  // EXTRA-AWARE applier. The scanRouter pre-merges `extra` against the live
  // person, but the grid's CIP slot-machine effect emits only-new-keys
  // `extra` patches (idType/idNumber/…), so a shallow {...prev, ...patch}
  // would clobber prior extras. Deep-merge one level + sub-merge
  // `extra.extracts` (per-doc-type permanent audit store) — same logic the
  // retired PdwShell docs-grid stage carried (cross-contamination fix,
  // Bill 2026-05-28).
  const applyCapturePatch = useCallback((patch: Partial<RaoPerson>): void => {
    setLocal((prev) => {
      const merged: RaoPerson = { ...prev, ...withMiddleInitial(patch) };
      if (patch.extra) {
        const prevExtra = (prev.extra ?? {}) as Record<string, unknown>;
        const patchExtra = patch.extra as Record<string, unknown>;
        const mergedExtra: Record<string, unknown> = { ...prevExtra, ...patchExtra };
        if (patchExtra.extracts || prevExtra.extracts) {
          const prevExtracts = (prevExtra.extracts ?? {}) as Record<string, unknown>;
          const patchExtracts = (patchExtra.extracts ?? {}) as Record<string, unknown>;
          mergedExtra.extracts = { ...prevExtracts, ...patchExtracts };
        }
        merged.extra = mergedExtra;
      }
      const display = [merged.firstName, merged.middleName, merged.lastName]
        .filter((x) => !!x)
        .join(' ')
        .trim();
      if (display) merged.fullName = display;
      if (prev.mode === 'existing') merged.dirty = true;
      return merged;
    });
    setHasChanges(true);
  }, []);

  // The hosted grid emits ITS complete running AttachedDoc set on every
  // capture/delete. Unlike the retired Stage-4 hop (authoritative replace),
  // the consolidated body may ALSO hold docs the grid doesn't render (e.g.
  // the relocated ADDR_PROOF, or doc-library hydrations that map to no tile)
  // — so we merge by docCode and drop ONLY codes the grid previously emitted
  // and has since deleted.
  const lastGridDocCodesRef = useRef<ReadonlySet<string>>(new Set());
  const handleGridDocsChange = useCallback((docs: ReadonlyArray<AttachedDoc>): void => {
    const codes = new Set(docs.map((d) => d.docCode));
    const removed: string[] = [];
    for (const c of lastGridDocCodesRef.current) {
      if (!codes.has(c)) removed.push(c);
    }
    lastGridDocCodesRef.current = codes;
    setLocal((prev) => {
      let next = prev;
      if (removed.length > 0) {
        const kept = readAttachedDocs(prev).filter((d) => !removed.includes(d.docCode));
        next = { ...prev, extra: { ...(prev.extra ?? {}), attachedDocs: kept } } as RaoPerson;
      }
      return mergeAttachedDocs(next, docs);
    });
    setHasChanges(true);
  }, []);

  // Grid capture-state — informational in the consolidated body (the
  // required/total counter renders inside the grid; cart-commit is the gate).
  const handleCaptureStateChange = useCallback(
    (_state: { capturedTileIds: ReadonlySet<string>; allRequiredCaptured: boolean }): void => {
      /* counter renders in-grid; no body-level gating (soft gate, Bill 2026-06-02) */
    },
    [],
  );

  // D2 — Add-new opens with citizenship UNSET; the deterministic tile grid
  // derives only after the path is picked (and, for NRA, the purpose — the
  // purpose drives the lawful-presence doc set).
  const stayPurposeVal = ((): string => {
    const v = (local.extra as Record<string, unknown> | undefined)?.['stayPurpose'];
    return typeof v === 'string' ? v : '';
  })();
  // Bill ruling 2026-06-12 (procedure-cited) — the Visitor/Tourist purpose is
  // NOT eligible ("Legacy Procedure - Non-Citizen ID Requirements.txt":
  // "Non-Acceptable Visas: B (Visitor visa / tourism), VWP"): selecting it
  // never satisfies the citizenship gate and never derives document tiles.
  const cohortReady =
    !!local.citizenshipStatus &&
    local.citizenshipStatus !== 'Other' &&
    (local.citizenshipStatus !== 'Non-Resident Alien' ||
      (stayPurposeVal !== '' && stayPurposeVal !== 'Visitor / Tourist'));

  // Hide-until-selected (Bill 2026-06-12) — on a FRESH add the modal shows
  // ONLY the Citizenship block until a citizenship tile is picked: documents
  // grid, CIP, profile, work-school, contact and verify all stay hidden and
  // their jump-scroll pills render disabled/dimmed. Edit-mode / review-add
  // (hydrated person) is unaffected.
  const citizenshipChosen =
    !!local.citizenshipStatus && local.citizenshipStatus !== 'Other';
  const sectionsHidden = mode === 'new' && !citizenshipChosen;

  const isChanged = useCallback(
    (field: string): boolean => {
      const a = (local as unknown as Record<string, unknown>)[field];
      const b = (person as unknown as Record<string, unknown>)[field];
      return a !== b && !(a == null && b == null);
    },
    [local, person],
  );

  // Save-gate surfaces. The citizenship gate preserves the retired Stage-2
  // cohort screen's invariant: a fresh-add can't reach the cart without a
  // citizenship path (and, for NRA, a purpose of stay) — the consolidated
  // body otherwise opens with citizenship UNSET (D2).
  const dupInCart =
    !!(local.cifId && cartCifIds?.includes(local.cifId)) && mode === 'review-add';
  // ── Supervisor promotion gate — applied at the WRITE, not at the ADD ─────
  // Bill + Collette, 2026-07-27: opening John's Minor account was impossible.
  // Scanning his DL sets the DOB, the DOB derives 'Minor', Core has him stored
  // as 'Individual', so the promotion gate fired and DISABLED "Add existing
  // w/ changes" — with no way to obtain approval in-flow. Skipping the DL scan
  // was the only way through, which is exactly backwards: the verified
  // document made the app less usable.
  //
  // This hook's own contract says the flag exists to "gate the CustMod" —
  // i.e. block MODIFYING the CIF in Core, not block putting an existing person
  // in a cart. Adding someone to a cart writes nothing to Core. So the gate no
  // longer blocks the add; it still shows its banner, and it still blocks a
  // save that carries edits.
  //
  // Note the promotion is not cosmetic: a Minor product requires the primary
  // to BE 'Minor' (AddToCartModal), so forcing the stored 'Individual' would
  // just move the dead end. Admin lever if this needs turning off entirely:
  // SystemSetting existingCif.supervisorOverrideRequired (defaults true).
  const supervisorGate = !!derivation.requiresSupervisorApproval && hasChanges;
  const citizenshipGate = mode === 'new' && !cohortReady;
  // ── Completeness gate (Bill 2026-07-23, Peter's-cart post-mortem) ────────
  // A NEW person could previously save with NO required-field check at all —
  // the missing DOB then tripped the Review screen's MISSING_DOB engine gate
  // with no name, no chip, and no way back. Enforce the SHARED spec here so
  // an incomplete person can never leave the modal. Existing-CIF people are
  // exempt (Core already has the record — mirrors the server commit-validate
  // skip), so this can never block an existing-customer edit.
  const missingRequired = local.cifId ? [] : missingRequiredIndividualFields(local);
  const completenessGate = missingRequired.length > 0;
  // Test-identity rules (Bill 2026-09-15): a half-labelled test customer cannot be saved.
  const identityViolation = testIdentityViolation(local, readTestIdentitySettings(config.systemSettings));
  const saveDisabled = dupInCart || supervisorGate || citizenshipGate || completenessGate || !!identityViolation;
  const saveDisabledReason = dupInCart
    ? `CIF ${local.cifId ?? ''} is already on this cart.`
    : supervisorGate
      ? 'Supervisor approval required before saving.'
      : citizenshipGate
        ? stayPurposeVal === 'Visitor / Tourist'
          // Bill ruling 2026-06-12 — "Legacy Procedure - Non-Citizen ID
          // Requirements.txt": B / VWP visitors are not eligible.
          ? 'B-visa and Visa-Waiver visitors are not eligible for account ownership — Contact your supervisor for more information.'
          : 'Select the citizenship status (and the purpose of stay for a Non-Resident Alien) to continue.'
        : completenessGate
          ? `Missing required: ${missingRequired.join(', ')}.`
          : identityViolation;

  // Notify the caller on every change so the outer footer/save dispatch
  // can read the latest draft. Includes the initial-mount fire so callers
  // see the seed + computed meta without having to mirror the logic.
  useEffect(() => {
    if (!onDraftChange) return;
    const meta: IndividualReviewBodyMeta = {
      hasChanges,
      dupInCart,
      saveDisabled,
      ...(saveDisabledReason ? { saveDisabledReason } : {}),
      activeTab,
    };
    onDraftChange(local, meta);
  }, [local, hasChanges, dupInCart, saveDisabled, saveDisabledReason, activeTab, onDraftChange]);

  // E1 (Bill 2026-06-03) — fire the early dup-CIF check the moment the tax id
  // reaches 9 digits, regardless of HOW it got there. The SSN-card OCR auto-fills
  // taxId programmatically, so the field's onBlur never fires — the silent
  // CustSrch backstop in IndividualIdentitySection was being skipped on the
  // scan path (the Widener smoke test: no existing-CIF detection fired). This
  // parent-level watcher covers BOTH manual entry and OCR auto-fill; the onBlur
  // handler remains as a secondary manual-entry trigger. A ref guards against
  // re-firing on every keystroke / re-render once a given SSN has been checked.
  const lastDupCheckedSsnRef = useRef<string | null>(null);
  // Bill 2026-06-12 (answer #2) — EARLY name+DOB trigger alongside the SSN one.
  // When no 9-digit SSN is present but first+last name AND a complete DOB are
  // entered on a fresh-new person, fire the early dup-check too (the host's
  // onEarlyDupCheck tightens name candidates to name+DOB and opens the compare
  // modal). De-duped by a separate key-ref so it fires at most once per distinct
  // name+DOB this session and doesn't loop on every keystroke.
  const lastDupCheckedNameDobRef = useRef<string | null>(null);
  useEffect(() => {
    if (!onEarlyDupCheck || mode !== 'new' || existingCustomer) return;
    const ssn = (local.taxId ?? '').replace(/\D/g, '');
    if (ssn.length === 9) {
      if (ssn !== lastDupCheckedSsnRef.current) {
        lastDupCheckedSsnRef.current = ssn;
        onEarlyDupCheck({ ...local, taxId: ssn });
      }
      return;
    }
    // No usable SSN → try the name+DOB early trigger.
    const dob = (local.dob ?? '').replace(/\D/g, '');
    if (local.firstName && local.lastName && dob.length >= 8) {
      const key = `${local.firstName}|${local.lastName}|${dob}`.toLowerCase();
      if (key !== lastDupCheckedNameDobRef.current) {
        lastDupCheckedNameDobRef.current = key;
        onEarlyDupCheck(local, (cleared) => setLocal((l) => ({ ...l, noTinClearedKey: cleared })));
      }
    }
  }, [local, mode, existingCustomer, onEarlyDupCheck]);

  // -------------------------------------------------------------------------
  // D2 — mode-dependent ceremony + consolidated jump-scroll order
  // -------------------------------------------------------------------------
  // Person-Modal consolidation (Bill 2026-06-11, LOCKED mockup v6) —
  // jump-scroll pills, ONE per rendered section, CITIZENSHIP first (the
  // block determines the document set), Documents second. The Origin
  // section/pill is GONE — everything it held lives in the Citizenship block.
  // NO 'review' pill (the inline sections ARE the review surface); 'ownership'
  // is a business concern, never shown for a personal cohort (Bill 2026-06-02).
  // Mode does not reorder pills — add-new vs existing-CIF differ only in the
  // per-section Mark-Reviewed ceremony.
  const tabOrder = useMemo<ReadonlyArray<PdwTabKey>>(
    () => ['citizenship', 'documents', 'cip', 'profile', 'work-school', 'contact', 'verify'],
    [],
  );
  // Mark Reviewed Correct + Review Completed modal are the existing-CIF
  // review-add ceremony only (DESIGN-BIBLE line 88).
  const reviewCeremony = mode === 'review-add' && !readOnly;

  // Addendum 6 §6.4 D-INLINE-EDIT — the per-section Edit/Done toggle is GONE;
  // every section renders its fields inline, always editable. Only the existing-
  // CIF "Mark Reviewed Correct" ceremony state remains.
  const isReviewed = useCallback((key: string): boolean => reviewedSections.has(key), [reviewedSections]);
  const toggleReviewed = useCallback((key: string): void => {
    setReviewedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Reviewable section keys (used by the Review-Completed modal to count
  // unmarked sections). CIP + the derived cards. 'origin' dropped with the
  // Origin section (consolidation, Bill 2026-06-11 — D2: REVIEWABLE_KEYS
  // lose 'origin').
  const REVIEWABLE_KEYS = ['cip', 'identity', 'addressContact', 'employment', 'documents'] as const;
  const unmarkedCount = reviewCeremony
    ? REVIEWABLE_KEYS.filter((k) => !reviewedSections.has(k)).length
    : 0;

  // -------------------------------------------------------------------------
  // T4 — read-only derived rows (computed from the draft)
  // -------------------------------------------------------------------------
  const ex = (local.extra ?? {}) as Record<string, unknown>;
  const exStr = (k: string): string => (typeof ex[k] === 'string' ? (ex[k] as string) : '');
  const maskedTaxId = local.taxId && local.taxId.length >= 4 ? `***-**-${local.taxId.slice(-4)}` : '';

  const identityRows: DerivedRow[] = [
    { label: 'Name', value: [local.firstName, local.middleName, local.lastName, local.suffix].filter(Boolean).join(' '), changed: isChanged('firstName') || isChanged('lastName') },
    { label: 'Date of birth', value: local.dob ?? '', changed: isChanged('dob') },
    { label: 'Gender', value: local.gender ?? '' },
    { label: `SSN / TIN (${local.tinCode ?? 'S'})`, value: maskedTaxId, changed: isChanged('taxId') },
  ];
  const addressContactRows: DerivedRow[] = [
    { label: 'Physical', value: [local.addr, local.city, local.state, local.zip].filter(Boolean).join(', '), changed: isChanged('addr') },
    { label: 'Mailing', value: [local.mailAddr, local.mailCity, local.mailState, local.mailZip].filter(Boolean).join(', ') },
    { label: 'Phone', value: local.phoneCell || local.phoneHome || local.phoneBus || '', changed: isChanged('phoneCell') },
    { label: 'Email', value: local.email ?? '', changed: isChanged('email') },
  ];
  const employmentRows: DerivedRow[] = [
    { label: 'Status', value: local.employmentStatus ?? '' },
    { label: 'Employer', value: local.employer ?? '', changed: isChanged('employer') },
    { label: 'Occupation', value: local.occupation ?? '' },
  ];
  const citizenshipRows: DerivedRow[] = [
    { label: 'Citizenship', value: local.citizenshipStatus ?? '', changed: isChanged('citizenshipStatus') },
    { label: 'Country', value: local.countryOfCitizenship ?? '' },
    ...(local.citizenshipStatus === 'Non-Resident Alien'
      ? [
          { label: 'Purpose of stay', value: stayPurposeVal },
          // No visa-class input — the class is READ from the captured visa
          // stamp (normalizeVisaType → person.visaType).
          { label: 'Visa (from captured visa)', value: [local.visaType, local.visaExpDt].filter(Boolean).join(' · ') },
        ]
      : []),
  ];

  // §5 eligibility — fires from the EXTRACTED visa class at scan time and is
  // ALSO surfaced on the Verify rollup (Bill 2026-06-11). Same catalog the
  // Citizenship block's red banner reads.
  const ineligibleVisa =
    local.citizenshipStatus === 'Non-Resident Alien' && isIneligibleVisaClass(local.visaType);

  // Addendum 6 §6.4 D-TABS-JUMP-SCROLL — ALL sections render at once; the pills
  // scroll to a section anchor (no tab-swap, nothing hidden/filtered). An
  // IntersectionObserver lights the pill for whichever section is in view.
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const jumpTo = useCallback((key: PdwTabKey): void => {
    setActiveTab(key);
    bodyRef.current
      ?.querySelector(`#pdwsec-${key}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    const sections = Array.from(root.querySelectorAll<HTMLElement>('[data-pdwsec]'));
    if (sections.length === 0) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        const key = visible ? (visible.target as HTMLElement).dataset['pdwsec'] : undefined;
        if (key) setActiveTab(key as PdwTabKey);
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.5, 1] },
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [tabOrder]);

  return (
    <div ref={bodyRef} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {dupInCart && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          CIF <strong>{local.cifId}</strong> is already on this cart — use Edit to update their record.
        </div>
      )}

      {/* Contract C4 — a MAJOR cross-document conflict (e.g. ID numbers don't
          match between documents) → red ALERT. Contributes a `fail` to
          Verify · ID scan (see personWithDerivedClearance). Anything that may
          indicate the IDs are non-genuine (North-Star §2.6). */}
      {majorConflicts.length > 0 && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--err-bg)',
            border: '1px solid var(--pcb-red, #b10505)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>⚠ Document conflict — review required.</strong>{' '}
          {majorConflicts
            .map((c) => `${c.field} differs across ${c.docs.join(' / ')}`)
            .join('; ')}
          . This may indicate a non-genuine ID — Verify · ID scan is blocked until resolved.
        </div>
      )}

      {/* Addendum 6 §6.1 / §6.4 D-TABS-JUMP-SCROLL + Step 5 — the 4-axis status
          ribbon + jump-scroll pills are STICKY at the top of the scroll body.
          Citizenship / Lawful Presence / Identity Verified / Tax ID react to the
          draft; the pills scroll to (never hide) their section. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          paddingBottom: 8,
          marginBottom: -4,
          background: 'var(--surface-card,#fff)',
          borderBottom: '1px solid var(--ink-100,#f1f3f5)',
        }}
      >
        <PdwStatusBadges person={local} />
        {/* Hide-until-selected (Bill 2026-06-12): while the fresh-add modal
            shows only the Citizenship block, every other pill is disabled +
            dimmed — there is no section to jump to yet. */}
        <PdwTabStrip
          activeTab={activeTab}
          onTabChange={jumpTo}
          tabOrder={tabOrder}
          tabState={{
            'citizenship': { status: cohortReady ? 'done' : 'unvisited' } as PdwTabState,
            'cip':         { status: (attachedDocs.length > 0 || hasOnFileCip) ? 'done' : 'unvisited', disabled: sectionsHidden } as PdwTabState,
            'profile':     { status: local.firstName && local.lastName && local.dob ? 'done' : 'unvisited', disabled: sectionsHidden } as PdwTabState,
            'contact':     { status: (local.mailAddr || local.email || local.phoneCell || local.phoneHome) ? 'done' : 'unvisited', disabled: sectionsHidden } as PdwTabState,
            // BACKLOG-0762 — J-2 EAD soft gate: Employed on a J-2 without a
            // captured EAD keeps the pill on ⚠ (incomplete), never blocks.
            'work-school': {
              status: j2EadGatePending(local)
                ? 'warn'
                : (local.employer || isStudentVisa) ? 'done' : 'unvisited',
              ...(j2EadGatePending(local) ? { warnCount: 1 } : {}),
              disabled: sectionsHidden,
            } as PdwTabState,
            'verify':      {
              status:
                existingCustomer || verifyStatus.status !== 'yellow'
                  ? (verifyStatus.status === 'green' ? 'done' : 'warn')
                  : 'unvisited',
              disabled: sectionsHidden,
            } as PdwTabState,
            'documents':   { status: attachedDocs.length > 0 ? 'done' : 'unvisited', disabled: sectionsHidden } as PdwTabState,
          }}
        />
      </div>

      {/* ════════ CITIZENSHIP — TOP (LOCKED mockup v6, Bill 2026-06-11) ════════
          Replaces the Stage-2 cohort screen (CitizenshipCohortGate, retired)
          AND the Origin-tab picker (CitizenshipPicker, retired). Progressive
          disclosure: US = done on select · Resident Alien = + country · NRA =
          country + purpose cards. NO visa-class input — the class is read off
          the captured visa stamp; eligibility fires from the EXTRACTED class. */}
      <div id="pdwsec-citizenship" data-pdwsec="citizenship" style={{ scrollMarginTop: 124 }}>
        <DerivedSectionCard
          title="Citizenship — Determines document capture requirements"
          rows={citizenshipRows}
          readOnly={readOnly}
        >
          <IndividualCitizenshipSection
            person={local}
            onChange={update}
            isChanged={isChanged}
            onStudentVisaChange={setIsStudentVisa}
            readOnly={readOnly}
            syncCustomerType={mode === 'new'}
          />
        </DerivedSectionCard>
      </div>

      {/* Hide-until-selected (Bill 2026-06-12) — on a FRESH add, everything
          below the Citizenship block (documents grid, CIP, profile,
          work-school, contact, verify) stays hidden until a citizenship tile
          is picked. Edit-mode / review-add hydrated persons render all
          sections as before. */}
      {!sectionsHidden && (
      <>
      {/* ════════ DOCUMENTS TO CAPTURE — deterministic, cohort-derived ════════
          The Stage-4 grid is HOSTED here (the separate PdwShell docs-grid stage
          is gone). Tiles re-derive live from the Citizenship block above. The
          classifier-first ReviewDropStrip and the duplicate DocumentSlotsSection
          are REMOVED from this body (deterministic tiles are the only capture
          entry — 03-DETERMINISTIC-TILES). Per-doc scan modal, CIP slot machine,
          and the required/total counter live inside the grid. */}
      <div id="pdwsec-documents" data-pdwsec="documents" style={{ scrollMarginTop: 124 }}>
        {cohortReady ? (
          <>
            {!(local.cifId ?? '').trim() && !readOnly && (
              <div className="pdw-docslots__nocif">
                <strong>No CIF resolved yet.</strong> Captures stage client-side and
                upload to ao_Documents automatically once this person has a Core
                Director CIF (assigned via CustAdd at commit, or via CustInq lookup).
              </div>
            )}
            <PdwStage4DocsGrid
              person={local}
              onChange={applyCapturePatch}
              onCaptureStateChange={handleCaptureStateChange}
              onAttachedDocsChange={handleGridDocsChange}
              readOnly={readOnly}
            />
          </>
        ) : (
          <div
            style={{
              border: '1px dashed var(--ink-200, #dfe5ec)',
              borderRadius: 8,
              padding: '14px 16px',
              fontSize: 12.5,
              color: 'var(--ink-600)',
              background: 'var(--surface-subtle, #f7f9fc)',
            }}
          >
            <strong>Documents to capture</strong> — the document set derives from the
            Citizenship selection above. Pick the citizenship status
            {local.citizenshipStatus === 'Non-Resident Alien' ? ' and the purpose of stay' : ''}
            {' '}and the tiles appear here.
          </div>
        )}
      </div>

      {/* CIP — always-inline (D-INLINE-EDIT). Self-manages expiry-forced fields. */}
      <div id="pdwsec-cip" data-pdwsec="cip" style={{ scrollMarginTop: 124 }}>
        <IndividualCipScanSection
          person={local}
          onChange={update}
          mode={sectionMode}
          isChanged={isChanged}
          readOnly={readOnly}
          cipIdPriority={cipIdPriorityRules}
          showMarkReviewed={reviewCeremony}
          reviewed={isReviewed('cip')}
          onToggleReviewed={() => toggleReviewed('cip')}
        />
      </div>

      <div id="pdwsec-profile" data-pdwsec="profile" style={{ scrollMarginTop: 124 }}>
        <DerivedSectionCard
          title="Profile / SSN-TIN"
          rows={identityRows}
          readOnly={readOnly}
          showMarkReviewed={reviewCeremony}
          reviewed={isReviewed('identity')}
          onToggleReviewed={() => toggleReviewed('identity')}
        >
          <IndividualIdentitySection
            person={local}
            onChange={update}
            mode={sectionMode}
            isChanged={isChanged}
            derivation={derivation}
            readOnly={readOnly}
            {...(onEarlyDupCheck && mode === 'new' && !existingCustomer
              ? { onSsnComplete: (ssn: string) => onEarlyDupCheck({ ...local, taxId: ssn }) }
              : {})}
          />
          {/* Consolidation (Bill 2026-06-11) — the NRA Alien_CType / W-8BEN
              ceremony moved here from the retired Origin section ("W-8BEN /
              W-9 ceremony stays on SSN-TIN per cohort" — LOCKED mockup v6).
              Renders only for Non-Resident Alien; its embedded duplicate
              capture grid + typed visa-class select are removed (tiles-only
              capture above; class read from the captured stamp). */}
          <IndividualNraSection
            person={local}
            onChange={update}
            isChanged={isChanged}
            readOnly={readOnly}
          />
        </DerivedSectionCard>
      </div>

      <div id="pdwsec-work-school" data-pdwsec="work-school" style={{ scrollMarginTop: 124 }}>
        <DerivedSectionCard
          title="Work / School"
          rows={employmentRows}
          readOnly={readOnly}
          showMarkReviewed={reviewCeremony}
          reviewed={isReviewed('employment')}
          onToggleReviewed={() => toggleReviewed('employment')}
        >
          <IndividualEmploymentSection
            person={local}
            onChange={update}
            isChanged={isChanged}
            isStudentVisa={isStudentVisa}
            readOnly={readOnly}
          />
        </DerivedSectionCard>
      </div>

      {/* ════════ ADDRESS & CONTACT — hosts the Address Verification capture
          (Bill 2026-06-11 relocation): the conditional tile renders HERE,
          beside the Physical/Mailing fields it corroborates — NOT in the top
          grid. Reactive "corroborated yet?" rule re-evaluates on every capture
          and every address-field edit; a matching DL suppresses it. */}
      <div id="pdwsec-contact" data-pdwsec="contact" style={{ scrollMarginTop: 124 }}>
        <DerivedSectionCard
          title="Address & Contact"
          rows={addressContactRows}
          readOnly={readOnly}
          showMarkReviewed={reviewCeremony}
          reviewed={isReviewed('addressContact')}
          onToggleReviewed={() => toggleReviewed('addressContact')}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(220px, 248px)',
              gap: 14,
              alignItems: 'start',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <AddressSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
              <ContactSection person={local} onChange={update} isChanged={isChanged} readOnly={readOnly} />
            </div>
            <AddressVerificationTile
              person={local}
              applyPatch={applyCapturePatch}
              onDocumentCaptured={registerAttachedDoc}
              readOnly={readOnly}
            />
          </div>
        </DerivedSectionCard>
      </div>

      {/* The Origin section is GONE (consolidation, Bill 2026-06-11) — its
          citizenship picker lives in the Citizenship block at top; the NRA
          Alien_CType ceremony lives under Profile / SSN-TIN. */}

      <div id="pdwsec-verify" data-pdwsec="verify" style={{ scrollMarginTop: 124 }}>
        {/* §5 eligibility on the Verify rollup — same scan-time rule the
            Citizenship block surfaces (extracted visa class vs the
            ineligible catalog). */}
        {ineligibleVisa && (
          <div
            role="alert"
            style={{
              padding: '10px 14px',
              background: 'var(--err-bg)',
              border: '1px solid var(--pcb-red, #b10505)',
              borderRadius: 6,
              fontSize: 13,
              color: 'var(--ink-900)',
              marginBottom: 8,
            }}
          >
            <strong>Ineligible Visa Type — {local.visaType}</strong> (read from the captured
            visa). B-1/B-2 tourist and VWP/ESTA visitors are not eligible to open accounts
            at PCB. This person cannot proceed without a valid long-term visa.
          </div>
        )}
        <VerifyRollupBanner person={personWithDerivedClearance} />
        <OfacScanSection
          person={local}
          cartId={cartId}
          onChange={update}
          readOnly={readOnly}
          comNameType="Indv"
          attachedDocs={attachedDocs}
        />
        <IndividualAmlInsightSection
          person={local}
          onChange={update}
          isChanged={isChanged}
          readOnly={readOnly}
          {...(cartMode !== undefined ? { cartMode } : {})}
        />
        <PepDesignationCard person={local} onChange={update} readOnly={readOnly} />
      </div>
      </>
      )}

      {/* D2 — review-add ceremony: "Review completed" skip-confirm. Lets the
          FSR mark the remaining unmarked sections reviewed in one step for a
          repeat customer / expediency. */}
      {reviewCeremony && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setShowReviewCompleted(true)}
            style={{
              fontSize: 13,
              fontWeight: 600,
              padding: '7px 16px',
              borderRadius: 6,
              border: 'none',
              background: 'var(--brand-fill)',
              color: 'var(--on-brand)',
              cursor: 'pointer',
            }}
          >
            Review completed…
          </button>
        </div>
      )}

      {showReviewCompleted && (
        <ModalShell
          open={showReviewCompleted}
          onClose={() => setShowReviewCompleted(false)}
          title="Review completed?"
          size="sm"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-700, #495057)' }}>
              {unmarkedCount > 0 ? (
                <>
                  <strong>{unmarkedCount}</strong> section{unmarkedCount === 1 ? '' : 's'} {unmarkedCount === 1 ? 'is' : 'are'}{' '}
                  not yet marked reviewed-correct. For a repeat customer you can accept the
                  pre-filled values as reviewed and proceed.
                </>
              ) : (
                <>All sections are marked reviewed-correct. You're ready to save.</>
              )}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                type="button"
                onClick={() => setShowReviewCompleted(false)}
                style={{
                  fontSize: 13,
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: '1px solid var(--ink-300, #ced4da)',
                  background: 'var(--surface-card, #fff)',
                  color: 'var(--ink-900, #1d1d1f)',
                  cursor: 'pointer',
                }}
              >
                Keep reviewing
              </button>
              <button
                type="button"
                onClick={() => {
                  setReviewedSections(new Set(REVIEWABLE_KEYS));
                  setShowReviewCompleted(false);
                }}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'var(--ok-fill)',
                  color: 'var(--on-status-fill)',
                  cursor: 'pointer',
                }}
              >
                Accept &amp; mark all reviewed
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
